// Campfire cooker block entity (MB.3). PURE (ENG-1).
//
// Unlike a furnace, a campfire takes no fuel and has no output slot: it holds up
// to four raw foods, each cooking independently over a fixed 30-second timer
// (Minecraft ignores the recipe's own time for campfires), and pops the finished
// food out into the world as a drop. Only items with a `campfire` cook recipe
// are accepted. Placing/lighting, soul campfires, fire damage, and the crossed-
// log model are deferred to MB.3-remainder.
import { type ItemStack, emptyStack, isEmpty, cloneStack } from '../inventory';
import { smeltResult, type CookKind } from '../smelting';
import type { Registries } from '../../registry/load';

export const CAMPFIRE_SLOTS = 4;
/** Ticks each food cooks (Minecraft: 600 gt = 30 s), independent of the recipe. */
export const CAMPFIRE_COOK_TICKS = 600;
const KINDS: readonly CookKind[] = ['campfire'];

export interface CampfireSlot {
  /** The raw item cooking here (empty = free slot). */
  item: ItemStack;
  /** Ticks it has cooked so far. */
  ticks: number;
}
export interface CampfireData {
  slots: CampfireSlot[];
}

export function newCampfire(): CampfireData {
  return { slots: Array.from({ length: CAMPFIRE_SLOTS }, () => ({ item: emptyStack(), ticks: 0 })) };
}

/** Raw foods a broken campfire scatters (whatever is still cooking). */
export function campfireContents(c: CampfireData): ItemStack[] {
  return c.slots.map((s) => s.item).filter((s) => !isEmpty(s));
}

export function cloneCampfire(c: CampfireData): CampfireData {
  return { slots: c.slots.map((s) => ({ item: isEmpty(s.item) ? emptyStack() : cloneStack(s.item), ticks: s.ticks })) };
}

/** True if `item` can be cooked on a campfire (has a `campfire` recipe). */
export function isCampfireCookable(reg: Registries, item: ItemStack): boolean {
  return !isEmpty(item) && smeltResult(reg, { item: item.item, count: 1 }, KINDS) !== null;
}

/**
 * Place one unit of `stack` into the first free slot, iff it's campfire-cookable
 * and a slot is free. Returns true when accepted (the host then consumes one
 * from the player's hand). Never stacks — each slot cooks a single item.
 */
export function addToCampfire(c: CampfireData, reg: Registries, stack: ItemStack): boolean {
  if (!isCampfireCookable(reg, stack)) return false;
  const free = c.slots.find((s) => isEmpty(s.item));
  if (!free) return false;
  free.item = { item: stack.item, count: 1 };
  free.ticks = 0;
  return true;
}

/**
 * Advance every occupied slot one tick. A slot that reaches the cook time yields
 * its cooked result (returned for the host to drop) and clears. Returns the
 * finished stacks this tick (empty array if none).
 */
export function tickCampfire(c: CampfireData, reg: Registries): ItemStack[] {
  const done: ItemStack[] = [];
  for (const slot of c.slots) {
    if (isEmpty(slot.item)) continue;
    const recipe = smeltResult(reg, slot.item, KINDS);
    if (!recipe) continue; // no recipe (shouldn't happen) — leave the raw item be
    slot.ticks++;
    if (slot.ticks >= CAMPFIRE_COOK_TICKS) {
      done.push({ item: recipe.item, count: 1 });
      slot.item = emptyStack();
      slot.ticks = 0;
    }
  }
  return done;
}
